/**
 * WorkspaceStagePanel — Stage panels for each of the 5 lifecycle stages.
 *
 * Phase 4.3 update: Data stage now uses DataTab with sub-rail.
 * Phase 4.6 update: Deliverables stage now uses DeliverablesTab with sub-rail.
 * Other stages remain as placeholders or use their respective components.
 */

import React, { Suspense } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { EmptyState } from '@/components/ui/EmptyState';
import {
  FileText,
  BarChart3,
  FileOutput,
  Scale,
  AlertTriangle,
  Gavel,
  Users,
  Stamp,
  Package,
  EyeOff,
} from 'lucide-react';
import { DataTab } from './DataTab';
import { StaffWorkbenchContent } from '../accountant/StaffWorkbenchContent';
import { EngineReviewLandingContent } from '../accountant/review/EngineReviewLandingContent';
import { EngineReviewDetailContent } from '../accountant/review/EngineReviewDetailContent';
import { AnomalyReviewContent } from '../accountant/AnomalyReviewContent';
import { ExportCenterContent } from '../accountant/ExportCenterContent';
import { IntakeReviewContent } from '../accountant/IntakeReviewContent';
import { DefenseReviewContent } from '../accountant/review/DefenseReviewContent';
import { SignOffReviewContent } from '../accountant/review/SignOffReviewContent';
import { WarRoomReviewContent } from '../accountant/WarRoomReviewContent';
import ExtractionCertifyControl from '../extraction/ExtractionCertifyControl';
import ExtractionTierTrail from '../extraction/ExtractionTierTrail';
// Type-only (erased at build): pulls no runtime module, so the wizard weight stays out
// of every non-DS bundle. The host itself is lazy (below).
import type { DataSpecialistWizardStage } from './DataSpecialistWizardHost';

// Lazy load DeliverablesTab to avoid circular deps and chunk split
const DeliverablesTab = React.lazy(() => import('@/pages/case/tabs/DeliverablesTab'));

// Attorney stage content — lazy so each stage's chunk splits and the attorney
// bundle doesn't pull them into the shell's initial load.
const AttorneyMethodologyDefenseView = React.lazy(() => import('@/pages/AttorneyMethodologyDefenseView'));
const AttorneyWarRoomContent = React.lazy(() => import('@/components/attorney/workspace/AttorneyWarRoomContent'));
const AttorneyFinalizeContent = React.lazy(() => import('@/components/attorney/workspace/AttorneyFinalizeContent'));
const AttorneyDeliverablesContent = React.lazy(() => import('@/components/attorney/workspace/AttorneyDeliverablesContent'));
const AttorneySettlementContent = React.lazy(() => import('@/components/attorney/workspace/AttorneySettlementContent'));
const AttorneyAnalysisContent = React.lazy(() => import('@/components/attorney/workspace/AttorneyAnalysisContent'));
const AttorneyIntakeContent = React.lazy(() => import('@/components/attorney/workspace/AttorneyIntakeContent'));
const AdjusterAnalysisTab = React.lazy(() => import('@/pages/analysis/AdjusterAnalysisTab'));
const AdjusterPositionContent = React.lazy(() => import('@/components/adjuster/workspace/AdjusterPositionContent'));
// DS wizard host — lazy so the acquired Wizard V2 steps (SidebarStep1–4, ~4k lines) never
// pull into the shell's initial bundle for the accountant/attorney/adjuster roles. Kept as
// ONE stable lazy type: the same component renders every wizard stage (client → event →
// transactions → payroll), so React preserves the instance across stage switches and the
// single useWizardState/useAutoSave pair lives on (Fable guardrail — no per-stage remount).
const DataSpecialistWizardHost = React.lazy(() => import('./DataSpecialistWizardHost'));

interface PanelProps {
  className?: string;
}

interface DataPanelProps extends PanelProps {
  caseId?: string;
}

export function IntakePanel({ className = '' }: StagePanelProps) {
  return (
    <div className={`p-8 ${className}`} data-testid="intake-panel">
      <EmptyState
        icon={<FileText className="w-12 h-12 text-[var(--color-brand)]" />}
        title="Intake"
        description="Case intake and initial document collection. This panel will contain document upload, checklist, and attestation workflows."
      />
    </div>
  );
}

export function DataPanel({ className = '', caseId }: StagePanelProps) {
  return (
    <div className={`h-full ${className}`} data-testid="data-panel">
      <DataTab caseId={caseId} />
    </div>
  );
}

export function AnalysisPanel({ className = '' }: StagePanelProps) {
  return (
    <div className={`p-8 ${className}`} data-testid="analysis-panel">
      <EmptyState
        icon={<BarChart3 className="w-12 h-12 text-[var(--color-brand)]" />}
        title="Analysis"
        description="Forensic analysis and engine review. This panel will contain War Room deliberation, adjuster analysis, and engine attestations."
      />
    </div>
  );
}

export function DeliverablesPanel({ className = '', caseId }: StagePanelProps) {
  return (
    <div className={`h-full ${className}`} data-testid="deliverables-panel">
      <Suspense
        fallback={
          <div className="p-8 text-center text-[var(--color-text-muted)]">
            Loading Deliverables...
          </div>
        }
      >
        <DeliverablesTab caseId={caseId} />
      </Suspense>
    </div>
  );
}

export function SettlementPanel({ className = '' }: StagePanelProps) {
  return (
    <div className={`p-8 ${className}`} data-testid="settlement-panel">
      <EmptyState
        icon={<Scale className="w-12 h-12 text-[var(--color-brand)]" />}
        title="Settlement"
        description="Settlement intelligence and negotiation tools. This panel will contain game theory analysis, vendor intelligence, and settlement strategies."
      />
    </div>
  );
}

/** Map of stage IDs to their panel components (default / attorney 5-stage set) */
export const STAGE_PANELS: Record<string, React.FC<StagePanelProps>> = {
  intake: IntakePanel,
  data: DataPanel,
  analysis: AnalysisPanel,
  deliverables: DeliverablesPanel,
  settlement: SettlementPanel,
};

/**
 * Accountant 8-stage scaffolds. Each stage FRAMES an existing routed page/tool
 * (org spec "zero net-new dashboards") — the `binds` note names it. The real
 * embedding lands in the per-stage batches; until then the scaffold renders an
 * honest EmptyState naming what will mount here (never a blank, never fake data).
 */
function makeScaffold(
  testid: string,
  title: string,
  binds: string,
  icon: React.ReactNode
): React.FC<StagePanelProps> {
  const Panel: React.FC<StagePanelProps> = ({ className = '' }) => (
    <div className={`p-8 ${className}`} data-testid={testid}>
      <EmptyState
        icon={icon}
        title={title}
        description={`Binds ${binds}. Wiring lands in a later batch of the accountant workspace build.`}
      />
    </div>
  );
  Panel.displayName = `AccountantScaffold(${testid})`;
  return Panel;
}

const iconCls = 'w-12 h-12 text-[var(--color-brand)]';

/**
 * Stage 2 (Reconcile) FRAMES the P&L Workbench: renders StaffWorkbenchContent bare
 * (no RoleDashboardLayout, no page header) inside the workspace stage — the same
 * component the standalone /dashboard/accountant/workbench/:reportId route uses
 * (extract-content pattern, zero net-new dashboards).
 */
export function ReconcilePanel({ className = '', caseId }: StagePanelProps) {
  return (
    <div className={`h-full ${className}`} data-testid="reconcile-panel">
      <StaffWorkbenchContent showPageHeader={false} reportId={caseId} />
    </div>
  );
}

/**
 * Stage 3 (Engines) FRAMES Engine Review: the 12-engine grid (landing), and — when
 * a `:panel` sub-route (= engineId) is present — the per-engine detail rendered
 * editable in place (in_house_edit). Same content components the standalone
 * /dashboard/accountant/review routes use (extract-content pattern). Back-to-grid
 * is the ENGINES stage chip in the strip; the detail's own backHref points there too.
 */
export function EnginesPanel({ caseId }: StagePanelProps) {
  const { panel } = useParams<{ panel?: string }>();
  const base = `/accountant/reports/${caseId}/engines`;
  if (panel) {
    return (
      <div className="h-full" data-testid="engines-panel">
        <EngineReviewDetailContent
          mode="in_house_edit"
          reportId={caseId}
          engineId={panel}
          backHref={base}
        />
      </div>
    );
  }
  return (
    <div className="h-full" data-testid="engines-panel">
      <EngineReviewLandingContent
        mode="in_house_readonly"
        reportId={caseId}
        showHeader={false}
        hrefForEngine={(id) => `${base}/${id}`}
      />
    </div>
  );
}

/**
 * Stage 4 (Anomalies) FRAMES Anomaly Review — the resolve queue + WhyFlagged SHAP,
 * bare inside the stage (extract-content). Same component the standalone
 * /dashboard/accountant/anomalies/:reportId route uses.
 */
export function AnomaliesPanel({ caseId }: StagePanelProps) {
  return (
    <div className="h-full" data-testid="anomalies-panel">
      <AnomalyReviewContent reportId={caseId} showHeader={false} />
    </div>
  );
}

/**
 * Stage 1 (Intake) FRAMES the intake composite — Extracted Documents +
 * Data Quality — bare inside the stage. Both are existing self-fetching
 * panels (ExtractedDocumentsPanel + DataQualityReportPanel); IntakeReviewContent
 * only supplies the segmented sub-nav (zero net-new dashboards).
 */
export function AccountantIntakePanel({ caseId }: StagePanelProps) {
  return (
    <div className="h-full" data-testid="intake-panel">
      <IntakeReviewContent reportId={caseId} showHeader={false} />
    </div>
  );
}

/**
 * Stage 5 (Defense) FRAMES the "defend the number" composite — Stress-Test
 * (Monte Carlo + Sensitivity), the Methodology/Daubert slate, and the Carrier
 * Counter-Model rebuttal workbench — bare inside the stage. All three are
 * existing surfaces reused via the extract-content pattern; DefenseReviewContent
 * only supplies the segmented sub-nav (zero net-new dashboards).
 */
export function DefensePanel({ caseId }: StagePanelProps) {
  return (
    <div className="h-full" data-testid="defense-panel">
      <DefenseReviewContent reportId={caseId} showHeader={false} />
    </div>
  );
}

/**
 * Stage 6 (War Room) FRAMES the six-agent deliberation as a ROLE-SHAPED
 * attestation gate for the accountant — the number-verdict, the accountant's
 * priced work queue with inline expert-input, the readiness/blocker gate, and the
 * witness-trail — bare inside the stage (extract-content). Reuses the existing
 * deliberation stack; NOT the raw /deliberation page. Zero net-new dashboards.
 */
export function WarRoomPanel({ caseId }: StagePanelProps) {
  const { pathname } = useLocation();
  // External CPA (accountant JWT) silo: the deliberation endpoints are Clerk-only and the
  // privileged strategy content is held back from external accountants, so show a neutral
  // state instead of firing calls that would 401 and toast. Clerk sessions are unchanged.
  const isExternalAccountant =
    pathname.startsWith('/accountant/reports/') && !!localStorage.getItem('accountant_token');
  if (isExternalAccountant) {
    return (
      <div className="h-full" data-testid="war-room-panel">
        <EmptyState
          icon={<EyeOff className="w-12 h-12" />}
          title="War Room deliberation isn't part of your shared workspace"
          description="The engagement team's internal deliberation stays private. Your review stages are unchanged — continue with any stage above."
        />
      </div>
    );
  }
  return (
    <div className="h-full" data-testid="war-room-panel">
      <WarRoomReviewContent reportId={caseId} showHeader={false} />
    </div>
  );
}

/**
 * Stage 7 (Sign-Off) FRAMES the attest composite — the readiness engine panel +
 * sign-off gate/Finalize CTA, and the signature manager — bare inside the stage.
 * All are existing self-fetching surfaces; SignOffReviewContent only supplies the
 * segmented sub-nav (zero net-new dashboards).
 */
export function SignOffPanel({ caseId }: StagePanelProps) {
  return (
    <div className="h-full" data-testid="sign-off-panel">
      <SignOffReviewContent reportId={caseId} showHeader={false} />
    </div>
  );
}

/**
 * Stage 8 (Deliver) FRAMES the Export Center — work-product Excel, court-ready
 * bundle, signed narratives — bare inside the stage (extract-content). Same
 * component the standalone /dashboard/accountant/export/:reportId route uses.
 */
export function DeliverPanel({ caseId }: StagePanelProps) {
  return (
    <div className="h-full" data-testid="deliver-panel">
      <ExportCenterContent reportId={caseId} showHeader={false} />
    </div>
  );
}

/** Accountant 8-stage panel set. `intake` frames the Intake composite. */
export const ACCOUNTANT_STAGE_PANELS: Record<string, React.FC<StagePanelProps>> = {
  intake: AccountantIntakePanel,
  reconcile: ReconcilePanel,
  engines: EnginesPanel,
  anomalies: AnomaliesPanel,
  defense: DefensePanel,
  'war-room': WarRoomPanel,
  'sign-off': SignOffPanel,
  deliver: DeliverPanel,
};

/**
 * Attorney 7-stage scaffolds (K3 taxonomy, 2026-09-01). Each FRAMES existing attorney
 * surfaces via the extract-content pattern — the real embedding lands in the per-stage
 * batches; until then each renders an honest EmptyState naming what will mount here
 * (never a blank, never fake data). `binds` names the tools K3 mapped to the stage.
 */
function makeAttorneyScaffold(
  testid: string,
  title: string,
  binds: string,
  icon: React.ReactNode
): React.FC<StagePanelProps> {
  const Panel: React.FC<StagePanelProps> = ({ className = '' }) => (
    <div className={`p-8 ${className}`} data-testid={testid}>
      <EmptyState
        icon={icon}
        title={title}
        description={`Binds ${binds}. Framed in a later batch of the attorney workspace migration.`}
      />
    </div>
  );
  Panel.displayName = `AttorneyScaffold(${testid})`;
  return Panel;
}

/**
 * Attorney Methodology stage FRAMES the Method Selection & Defense view bare inside
 * the stage — the SAME self-contained component the legacy methodology tab and the
 * /attorney/case/:caseId route mount (it reads caseId via useParams, self-fetches,
 * and already composes the Election slate + Carrier Counter-Model). Extract-content,
 * zero net-new (K3: Election · Counter-Model are this component's own two sections).
 */
export function AttorneyMethodologyPanel({}: StagePanelProps) {
  return (
    <div className="h-full" data-testid="attorney-methodology-panel">
      <Suspense
        fallback={<div className="p-8 text-center text-[var(--color-text-muted)]">Loading Methodology…</div>}
      >
        <AttorneyMethodologyDefenseView />
      </Suspense>
    </div>
  );
}

/** Attorney Intake stage: Overview · Coverage · Parties · Documents · Client Portal. */
export function AttorneyIntakePanel({ caseId }: StagePanelProps) {
  return (
    <div className="h-full" data-testid="attorney-intake-panel">
      <Suspense
        fallback={<div className="p-8 text-center text-[var(--color-text-muted)]">Loading Intake…</div>}
      >
        <AttorneyIntakeContent caseId={caseId} />
      </Suspense>
    </div>
  );
}

/** Attorney Analysis stage: Strength · Carrier · Distribution (consumption stage). */
export function AttorneyAnalysisPanel({ caseId }: StagePanelProps) {
  return (
    <div className="h-full" data-testid="attorney-analysis-panel">
      <Suspense
        fallback={<div className="p-8 text-center text-[var(--color-text-muted)]">Loading Analysis…</div>}
      >
        <AttorneyAnalysisContent caseId={caseId} />
      </Suspense>
    </div>
  );
}

/** Adjuster Analysis stage: reuses the existing AdjusterAnalysisTab (self-fetching by reportId). */
export function AdjusterAnalysisPanel({ caseId }: StagePanelProps) {
  return (
    <div className="h-full" data-testid="adjuster-analysis-panel">
      <Suspense
        fallback={<div className="p-8 text-center text-[var(--color-text-muted)]">Loading Analysis…</div>}
      >
        <AdjusterAnalysisTab reportId={caseId ?? ''} />
      </Suspense>
    </div>
  );
}

/** Adjuster Position stage: acknowledge the carrier position (who/when). FL-4 4.4. */
export function AdjusterPositionPanel({ caseId }: StagePanelProps) {
  return (
    <div className="h-full" data-testid="adjuster-position-panel">
      <Suspense
        fallback={<div className="p-8 text-center text-[var(--color-text-muted)]">Loading Position…</div>}
      >
        <AdjusterPositionContent caseId={caseId} />
      </Suspense>
    </div>
  );
}

/** Adjuster War Room stage: leads with the ai:adjuster counterpart + the
 * defendant-attorney attack preview, then the shared consensus (generalized
 * WarRoomReviewContent, role="adjuster"). Both sides may be the client, so the
 * full deliberation is shown (DECISION 1: single-tenant, full picture). */
export function AdjusterWarRoomPanel({ caseId }: StagePanelProps) {
  return (
    <div className="h-full" data-testid="adjuster-war-room-panel">
      <WarRoomReviewContent reportId={caseId} role="adjuster" showHeader={false} />
    </div>
  );
}

/** Attorney Settlement stage: Valuation · Modeling · Leverage · Posture (heaviest). */
export function AttorneySettlementPanel({ caseId }: StagePanelProps) {
  return (
    <div className="h-full" data-testid="attorney-settlement-panel">
      <Suspense
        fallback={<div className="p-8 text-center text-[var(--color-text-muted)]">Loading Settlement…</div>}
      >
        <AttorneySettlementContent caseId={caseId} />
      </Suspense>
    </div>
  );
}

/** Attorney Deliverables stage: Demand Package + Carrier Correspondence generators (DRAFT badges). */
export function AttorneyDeliverablesPanel({ caseId }: StagePanelProps) {
  return (
    <div className="h-full" data-testid="attorney-deliverables-panel">
      <Suspense
        fallback={<div className="p-8 text-center text-[var(--color-text-muted)]">Loading Deliverables…</div>}
      >
        <AttorneyDeliverablesContent caseId={caseId} />
      </Suspense>
    </div>
  );
}

/** Attorney War-Room stage: read-only Brief (default) + link to the full deliberation room. */
export function AttorneyWarRoomPanel({ caseId }: StagePanelProps) {
  return (
    <div className="h-full" data-testid="attorney-war-room-panel">
      <Suspense
        fallback={<div className="p-8 text-center text-[var(--color-text-muted)]">Loading War-Room…</div>}
      >
        <AttorneyWarRoomContent caseId={caseId} />
      </Suspense>
    </div>
  );
}

/** Attorney Finalize stage: Engine Audit → Export (PDF/DOCX) → Admin (Re-run, owner/admin). */
export function AttorneyFinalizePanel({ caseId }: StagePanelProps) {
  return (
    <div className="h-full" data-testid="attorney-finalize-panel">
      <Suspense
        fallback={<div className="p-8 text-center text-[var(--color-text-muted)]">Loading Finalize…</div>}
      >
        <AttorneyFinalizeContent caseId={caseId} />
      </Suspense>
    </div>
  );
}

/** Attorney 7-stage panel set (scaffolds until each stage is framed). */
export const ATTORNEY_STAGE_PANELS: Record<string, React.FC<StagePanelProps>> = {
  intake: AttorneyIntakePanel,
  analysis: AttorneyAnalysisPanel,
  methodology: AttorneyMethodologyPanel,
  settlement: AttorneySettlementPanel,
  deliverables: AttorneyDeliverablesPanel,
  'war-room': AttorneyWarRoomPanel,
  finalize: AttorneyFinalizePanel,
};

export const ADJUSTER_STAGE_PANELS: Record<string, React.FC<StagePanelProps>> = {
  analysis: AdjusterAnalysisPanel,
  'war-room': AdjusterWarRoomPanel,
  position: AdjusterPositionPanel,
};

/** Props for all stage panels (includes optional caseId) */
export interface StagePanelProps {
  className?: string;
  caseId?: string;
}

/**
 * DS Documents stage (formerly "extraction", renamed in the B1 spine): the per-case
 * document extraction review — the 4-tier trail + the fail-closed certify gate. Reuses
 * the two existing extraction components bare inside the stage (the shell provides the
 * header + stage strip). The Entries view (ExtractedDocumentsPanel + FNOL) lands in a
 * later batch as the left-rail second view; today the Output view is the whole panel.
 * Zero net-new.
 */
export function DataSpecialistExtractionPanel({ caseId }: StagePanelProps) {
  return (
    <div className="p-6 lg:p-8 max-w-[1200px] mx-auto" data-testid="ds-extraction-panel">
      {/* Certify command bar — button right, blockers strip full-width beneath, above the list. */}
      <ExtractionCertifyControl reportId={caseId ?? ''} className="mb-6" />
      <ExtractionTierTrail caseId={caseId ?? ''} />
    </div>
  );
}

/** DS scaffolds — framed in later batches (Handoff=release-to-accountant). Honest
 *  EmptyState, never fake data. */
function makeDataSpecialistScaffold(testid: string, title: string, description: string): React.FC<StagePanelProps> {
  const Panel: React.FC<StagePanelProps> = ({ className = '' }) => (
    <div className={`p-8 ${className}`} data-testid={testid}>
      <EmptyState icon={<FileText className="w-12 h-12" />} title={title} description={description} />
    </div>
  );
  Panel.displayName = `DataSpecialistScaffold(${testid})`;
  return Panel;
}

const DS_WIZARD_STAGE_IDS = ['client', 'event', 'transactions', 'payroll'] as const;

/**
 * DS wizard stages (Client · Event · Transactions · Payroll) — the acquired Wizard V2
 * steps 1–4 mounted chromeless inside the shell (B1, 2026-09-05). The same component
 * serves all four stages: it reads the active stage from the route param and hands it to
 * the host, which owns the wizard state ONCE and reconciles the step to the route. Steps
 * 5–7 are intentionally NOT acquired (owner ruling: DS doesn't need them; the clamped
 * navigator blocks any auto-advance past payroll).
 */
export function DataSpecialistWizardPanel({ caseId }: StagePanelProps) {
  const { stage } = useParams<{ stage?: string }>();
  const wizardStage: DataSpecialistWizardStage =
    (DS_WIZARD_STAGE_IDS as readonly string[]).includes(stage ?? '')
      ? (stage as DataSpecialistWizardStage)
      : 'client';
  return (
    <div className="h-full" data-testid="ds-wizard-panel">
      <Suspense
        fallback={<div className="p-8 text-center text-[var(--color-text-muted)]">Loading…</div>}
      >
        <DataSpecialistWizardHost caseId={caseId ?? ''} stage={wizardStage} />
      </Suspense>
    </div>
  );
}

/** DS War Room stage: leads with BOTH DS counterparts — ai:data_specialist
 * (Data Readiness) and ai:document_auditor (Doc Health) — as two hero cards, then
 * the shared consensus (generalized WarRoomReviewContent, role="data_specialist").
 * The two-seat hero grid is driven by WARROOM_COUNTERPARTS['data_specialist']. */
export function DataSpecialistWarRoomPanel({ caseId }: StagePanelProps) {
  return (
    <div className="h-full" data-testid="ds-war-room-panel">
      <WarRoomReviewContent reportId={caseId} role="data_specialist" showHeader={false} />
    </div>
  );
}

/**
 * DS 6-stage panel set (B1 + K3-approved spine, 2026-09-05). `documents` frames the
 * extraction-tier trail + certify gate (existing, live); client/event/transactions/payroll
 * are the acquired Wizard V2 steps via the single wizard host; handoff is the release gate
 * (scaffold — framed in a later batch). Validation is NOT a peer stage (its checks are
 * Transactions ViewModes; its gate folds into Handoff — K3 gate).
 */
export const DATA_SPECIALIST_STAGE_PANELS: Record<string, React.FC<StagePanelProps>> = {
  documents: DataSpecialistExtractionPanel,
  client: DataSpecialistWizardPanel,
  event: DataSpecialistWizardPanel,
  transactions: DataSpecialistWizardPanel,
  payroll: DataSpecialistWizardPanel,
  'war-room': DataSpecialistWarRoomPanel,
  handoff: makeDataSpecialistScaffold('ds-handoff-panel', 'Handoff', 'Release the case to the accountant queue. Framed in a later batch.'),
};

/**
 * Get the appropriate panel component for a stage ID. Role-aware: the accountant
 * role uses the 8-stage set; every other role keeps the existing 5-stage set
 * (unchanged — attorney/default behavior is byte-identical).
 */
export function getPanelForStage(stage: string, role?: string): React.FC<StagePanelProps> {
  const key = stage.toLowerCase();
  if (role === 'accountant') {
    return ACCOUNTANT_STAGE_PANELS[key] || IntakePanel;
  }
  if (role === 'attorney') {
    return ATTORNEY_STAGE_PANELS[key] || IntakePanel;
  }
  if (role === 'adjuster') {
    return ADJUSTER_STAGE_PANELS[key] || IntakePanel;
  }
  if (role === 'data_specialist') {
    return DATA_SPECIALIST_STAGE_PANELS[key] || DataSpecialistExtractionPanel;
  }
  return STAGE_PANELS[key] || IntakePanel;
}

export default {
  IntakePanel,
  DataPanel,
  AnalysisPanel,
  DeliverablesPanel,
  SettlementPanel,
  STAGE_PANELS,
  getPanelForStage,
};
